import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import { useAuth } from "../contexts/AuthContext";
import { mockApiService, shouldUseMockData, mockLockets } from "../mockData/mockLocketData";
import { CreditCard as Edit3, LogOut, Lock, CircleAlert as AlertCircle, TriangleAlert as AlertTriangle, ClipboardList, Phone, Activity, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";
import LanguageSwitcher from "../components/shared/LanguageSwitcher";
import { useLanguage } from "../contexts/LanguageContext";

const PublicEmergencyInfoPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();
  const { t } = useLanguage();
  const scrollContainerRef = React.useRef(null);
  const [sections, setSections] = useState([]);
  const [customerName, setCustomerName] = useState("");
  const [ownerPhoto, setOwnerPhoto] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [showReportDisclaimer, setShowReportDisclaimer] = useState(false);
  const [showPhotoLightbox, setShowPhotoLightbox] = useState(false);
  const [callConfirm, setCallConfirm] = useState(null); // { name, number }

  useEffect(() => {
    const fetchInfo = async () => {
      setLoading(true);
      setError(null);
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      const applyResponse = (res) => {
        const mockSections = (res.data?.sections || []).sort((a, b) => {
          const priorityDiff = (priorityOrder[a.priority] || 1) - (priorityOrder[b.priority] || 1);
          if (priorityDiff !== 0) return priorityDiff;
          return a.order - b.order;
        });
        setSections(mockSections);
        setCustomerName(res.data?.customer?.name || "");
        setOwnerPhoto(res.data?.customer?.photo || null);
      };

      try {
        let res;
        let usedMock = false;

        if (shouldUseMockData()) {
          res = await mockApiService.getPublicEmergencyInfo(locketId);
          usedMock = true;
          applyResponse(res);
        } else {
          try {
            res = await api.get(`/emergency/${locketId}/public`);
            applyResponse(res);
          } catch (apiErr) {
            const status = apiErr.response?.status;
            const isNotFoundOrNotActivated = status === 404 || status === 400;
            const isNetworkError = !apiErr.response && apiErr.message;
            const upperId = (locketId || "").toUpperCase();
            const isDemoId = Object.keys(mockLockets || {}).includes(upperId);
            if ((isNotFoundOrNotActivated || isNetworkError) && isDemoId) {
              res = await mockApiService.getPublicEmergencyInfo(locketId);
              usedMock = true;
              applyResponse(res);
            } else {
              throw apiErr;
            }
          }
        }

        if (token) {
          try {
            if (usedMock || shouldUseMockData()) {
              await mockApiService.getCustomerProfile(locketId, token);
            } else {
              await api.get(`/qr/${locketId}/profile`, {
                headers: { Authorization: `Bearer ${token}` },
              });
            }
            setIsOwner(true);
          } catch (ownerErr) {
            setIsOwner(false);
          }
        }
      } catch (err) {
        const errorMessage = err.response?.data?.error || err.data?.error || "Failed to load emergency info.";
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [locketId, token]);

  useEffect(() => {
    const handleTokenExpired = () => {
      logout();
      setIsOwner(false);
    };
    window.addEventListener("tokenExpired", handleTokenExpired);
    return () => window.removeEventListener("tokenExpired", handleTokenExpired);
  }, [logout]);

  // Extract emergency contact phone numbers from sections for one-tap calling
  const getEmergencyPhones = () => {
    const phones = [];
    for (const section of sections) {
      if (section.category === "emergency") {
        for (const item of section.items) {
          const phoneMatch = item.value.match(/[6-9]\d{9}/);
          if (phoneMatch) {
            phones.push({ name: item.key, number: phoneMatch[0] });
          }
        }
      }
    }
    return phones;
  };

  const getSectionConfig = (color, priority) => {
    const configs = {
      red: {
        header: "#dc2626",
        headerGradient: "from-red-600 to-red-700",
        bg: "bg-red-50",
        border: "border-red-200",
        labelColor: "text-red-700",
        badge: "bg-red-100 text-red-800",
      },
      orange: {
        header: "#f97316",
        headerGradient: "from-orange-500 to-orange-600",
        bg: "bg-orange-50",
        border: "border-orange-200",
        labelColor: "text-orange-700",
        badge: "bg-orange-100 text-orange-800",
      },
      teal: {
        header: "#14b8a6",
        headerGradient: "from-teal-500 to-teal-600",
        bg: "bg-teal-50",
        border: "border-teal-200",
        labelColor: "text-teal-700",
        badge: "bg-teal-100 text-teal-800",
      },
      blue: {
        header: "#3b82f6",
        headerGradient: "from-blue-500 to-blue-600",
        bg: "bg-blue-50",
        border: "border-blue-200",
        labelColor: "text-blue-700",
        badge: "bg-blue-100 text-blue-800",
      },
      gray: {
        header: "#475569",
        headerGradient: "from-slate-500 to-slate-600",
        bg: "bg-slate-50",
        border: "border-slate-200",
        labelColor: "text-slate-700",
        badge: "bg-slate-100 text-slate-800",
      },
    };
    return configs[color] || configs.gray;
  };

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border border-slate-200 p-8 text-center max-w-sm w-full">
          <div className="mb-3 animate-pulse">
            <Activity size={40} className="text-red-600 mx-auto" />
          </div>
          <div className="text-base font-bold text-slate-800">{t("public.loadingDetails")}</div>
        </div>
      </div>
    );
  }

  // --- ERROR STATE ---
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-md border-2 border-red-300 p-6 text-center max-w-md w-full">
          <AlertCircle size={40} className="text-red-600 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-red-700 mb-1">{t("public.errorTitle")}</h2>
          <p className="text-slate-600 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const emergencyPhones = getEmergencyPhones();

  return (
    <div
      ref={scrollContainerRef}
      className="min-h-screen overflow-y-auto overflow-x-hidden bg-slate-50"
    >
      {/* Language sticky button */}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col items-center gap-2">
        <LanguageSwitcher />
      </div>

      {/* Top Navigation Bar — compact */}
      <div className="sticky top-0 z-40 bg-slate-50 border-b border-slate-200/50">
        <div className="max-w-3xl mx-auto px-3 py-1.5 md:px-6 md:py-2">
          <div className="flex justify-between items-center">
            <BrandLogo logoClassName="h-7 md:h-9" />
            <div className="flex gap-2 items-center">
              {isOwner ? (
                <button
                  onClick={() => {
                    logout();
                    setIsOwner(false);
                  }}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors duration-150"
                >
                  <LogOut size={15} />
                  <span>{t("public.logout")}</span>
                </button>
              ) : (
                <button
                  onClick={() => navigate(`/${locketId}/edit`)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors duration-150"
                >
                  <Lock size={15} />
                  <span className="hidden sm:inline">{t("emergency.ownerLogin")}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 pb-4 md:px-6">
        {/* 1. Quick actions: Report Emergency + 108 — FIRST, above everything */}
        <div className="mt-2 mb-2">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setShowReportDisclaimer(true)}
              className="flex flex-row items-center justify-center gap-2 rounded-lg bg-red-600 hover:bg-red-700 text-white p-3 min-h-[56px] shadow-md  transition-colors duration-150"
            >
              <AlertTriangle size={20} className="shrink-0" />
              <span className="text-sm font-bold leading-tight">{t("public.reportEmergency")}</span>
            </button>
            <a
              href="tel:108"
              className="flex flex-row items-center justify-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white p-3 min-h-[56px] shadow-md  transition-colors duration-150"
            >
              <Phone size={20} className="shrink-0" />
              <div className="text-center">
                <span className="text-base font-extrabold block leading-none">108</span>
                <span className="text-xs font-semibold leading-none">Ambulance</span>
              </div>
            </a>
          </div>
        </div>

        {/* 2+3. Merged: Emergency Info header + Owner photo */}
        <div id="emergency-header" className="mb-2 bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2.5">
            {/* Photo — clickable to expand */}
            <button
              type="button"
              onClick={() => (ownerPhoto || customerName) && setShowPhotoLightbox(true)}
              className="shrink-0 focus:outline-none"
              aria-label="View photo fullscreen"
            >
              {ownerPhoto ? (
                <img
                  src={ownerPhoto}
                  alt={customerName || "Owner"}
                  className="w-20 h-20 rounded-lg object-cover border-2 border-slate-200"
                />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-red-100 border-2 border-red-200 flex items-center justify-center">
                  <span className="text-3xl font-extrabold text-red-400">
                    {customerName ? customerName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
                  </span>
                </div>
              )}
            </button>
            {/* Right: name first, then title */}
            <div className="min-w-0 flex-1">
              {customerName && (
                <p className="text-base font-extrabold text-slate-900 leading-snug mb-0.5">{customerName}</p>
              )}
              <p className="text-xs font-semibold text-slate-500 leading-tight">{t("public.medicalInfoTitle")}</p>
            </div>
          </div>
          {/* Warning strip at bottom */}
          <div className="bg-amber-50 border-t border-amber-200 px-3 py-1.5 flex items-center gap-1.5">
            <span className="text-amber-600 text-sm shrink-0">⚠️</span>
            <p className="text-xs font-semibold text-amber-800">{t("public.ownerValidationNote")}</p>
          </div>
        </div>

        {/* 4. Emergency contacts — tap opens confirmation modal */}
        {emergencyPhones.length > 0 && (
          <div className="mb-2">
            <div className="flex flex-col gap-1.5">
              {emergencyPhones.map((phone, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCallConfirm({ name: phone.name, number: phone.number })}
                  className="flex items-center gap-3 rounded-lg bg-white border border-slate-200 hover:border-green-300 hover:bg-green-50 px-3 py-2.5 shadow-sm transition-colors duration-150 w-full text-left"
                >
                  <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                    <Phone size={16} className="text-green-700" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <span className="text-sm font-bold text-slate-900 block">{phone.name}</span>
                    <span className="text-xs font-mono text-slate-500">+91 {phone.number}</span>
                  </div>
                  <span className="text-xs font-bold text-green-700 shrink-0">CALL</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* 4. Emergency Info Sections */}
        {sections.length === 0 ? (
          <div className="mb-2">
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 text-center">
              <ClipboardList size={36} className="text-slate-400 mx-auto mb-2" />
              <h3 className="text-base font-bold text-slate-700 mb-1">{t("public.noDetailsTitle")}</h3>
              <p className="text-slate-500 text-sm">{t("public.noDetailsDesc")}</p>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {sections.map((section, sectionIdx) => {
              const config = getSectionConfig(section.color, section.priority);
              const isHighPriority = section.priority === "high";
              const isExpanded = expandedSections[sectionIdx] !== false;

              return (
                <div
                  key={sectionIdx}
                  id={sectionIdx === 0 ? "emergency-sections" : `section-${sectionIdx}`}
                  className={`bg-white rounded-lg shadow-sm overflow-hidden ${
                    isHighPriority ? "border-2 border-red-300 shadow-red-100" : "border border-slate-200"
                  }`}
                >
                  {/* Section Header */}
                  <button
                    type="button"
                    onClick={() => setExpandedSections((prev) => ({ ...prev, [sectionIdx]: !isExpanded }))}
                    aria-expanded={isExpanded}
                    aria-controls={`public-section-content-${sectionIdx}`}
                    className="w-full px-3 py-2 md:px-4 md:py-2.5 flex items-center justify-between gap-2 border-none cursor-pointer min-h-[40px] hover:opacity-95 transition-opacity"
                    style={{
                      background: `linear-gradient(135deg, ${config.header} 0%, ${config.header}ee 100%)`,
                    }}
                  >
                    <div className="flex-1 flex items-center justify-center gap-1.5">
                      {section.icon && <span className="text-lg shrink-0">{section.icon}</span>}
                      <p
                        className={`text-white font-bold uppercase tracking-wider ${
                          isHighPriority ? "text-sm md:text-base" : "text-xs md:text-sm"
                        }`}
                      >
                        {section.name}
                      </p>
                    </div>
                    <span className="shrink-0 text-white" aria-label={isExpanded ? "Collapse section" : "Expand section"}>
                      {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </span>
                  </button>

                  {/* Section Items */}
                  <div
                    id={`public-section-content-${sectionIdx}`}
                    className="p-2 md:p-3"
                    style={{ display: isExpanded ? "block" : "none" }}
                  >
                    <div className="grid grid-cols-2 gap-1.5">
                      {section.items.map((item, itemIdx) => {
                        const rawPhone = (item.value || "").toString().replace(/\s/g, "").replace(/-/g, "");
                        const phoneMatch = rawPhone.match(/^[+]?[\d]{10,}$/);
                        const telHref = phoneMatch
                          ? rawPhone.startsWith("+")
                            ? `tel:${rawPhone}`
                            : rawPhone.length === 10
                              ? `tel:+91${rawPhone}`
                              : `tel:${rawPhone}`
                          : null;
                        const tileContent = (
                          <>
                            {telHref && (
                              <div className="flex justify-center mb-0.5 shrink-0">
                                <span className="rounded-full p-1 bg-green-100 text-green-700">
                                  <Phone size={12} />
                                </span>
                              </div>
                            )}
                            <span
                              className={`text-[10px] font-bold uppercase tracking-wide block text-center mb-0.5 shrink-0 ${config.labelColor}`}
                            >
                              {item.key}
                            </span>
                            <span
                              className={`font-semibold text-slate-900 text-center leading-snug block break-words overflow-hidden px-0.5 w-full ${
                                isHighPriority ? "text-xs md:text-sm" : "text-xs"
                              }`}
                            >
                              {item.value}
                            </span>
                          </>
                        );
                        const tileClass = `${config.bg} rounded-lg p-2 min-h-[48px] border ${config.border} flex flex-col items-center justify-center transition-colors duration-150 `;
                        return telHref ? (
                          <a
                            key={itemIdx}
                            href={telHref}
                            className={`${tileClass} hover:ring-2 hover:ring-green-500/50 cursor-pointer`}
                            aria-label={`Call ${item.key}`}
                          >
                            {tileContent}
                          </a>
                        ) : (
                          <div key={itemIdx} className={tileClass}>
                            {tileContent}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* 5. Footer actions: Nearby services + Owner edit */}
        <div className="mt-2 space-y-1.5">
          <button
            onClick={() => navigate(`/${locketId}/nearby`)}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-white border border-blue-200 hover:border-blue-400 hover:bg-blue-50/50 px-3 py-2.5 shadow-sm transition-colors duration-150"
          >
            <MapPin size={16} className="text-blue-600 shrink-0" />
            <span className="text-sm font-bold text-slate-900">Nearby Hospitals &amp; Services</span>
          </button>
          {isOwner && (
            <button
              onClick={() => navigate(`/${locketId}/info`)}
              className="w-full flex items-center justify-center gap-2 rounded-lg bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 px-3 py-2.5 shadow-sm  transition-colors duration-150"
            >
              <Edit3 size={16} className="text-blue-600 shrink-0" />
              <span className="text-sm font-bold text-slate-900">{t("public.editInformation")}</span>
            </button>
          )}

          {/* Footer Notice */}
          <div className="bg-white rounded-lg border border-slate-200 p-2 text-center">
            <p className="text-xs text-slate-500">{t("public.footerTagline")}</p>
          </div>
        </div>
      </div>

      {/* Emergency call confirmation modal */}
      {callConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4">
          <div className="bg-white w-full max-w-sm sm:rounded-lg overflow-hidden rounded-t-2xl">

            {/* Large photo at top */}
            <div className="relative">
              {ownerPhoto ? (
                <img
                  src={ownerPhoto}
                  alt={customerName || "Owner"}
                  className="w-full h-56 object-cover"
                />
              ) : (
                <div className="w-full h-56 bg-red-100 flex items-center justify-center">
                  <span className="text-7xl font-extrabold text-red-300">
                    {customerName ? customerName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
                  </span>
                </div>
              )}
              {/* Red header overlay at top */}
              <div className="absolute top-0 left-0 right-0 bg-red-600 px-4 py-2.5 flex items-center gap-2">
                <AlertTriangle size={16} className="text-white shrink-0" />
                <p className="text-white font-bold text-sm tracking-wide uppercase">Emergency Call</p>
              </div>
              {/* Name overlay at bottom of photo */}
              <div className="absolute bottom-0 left-0 right-0 bg-black/60 px-4 py-2.5">
                <p className="text-white/70 text-xs font-semibold uppercase tracking-wide leading-none mb-0.5">Calling for</p>
                <p className="text-white text-lg font-extrabold leading-tight">{customerName || "this person"}</p>
              </div>
            </div>

            {/* Warning message */}
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-100">
              <p className="text-sm text-slate-700 leading-relaxed">
                You are about to call this person's emergency contact. Proceed only in case of a{" "}
                <span className="font-extrabold text-red-600">REAL emergency</span>.
              </p>
            </div>

            {/* Contact card */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-2.5">
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                  <Phone size={16} className="text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-green-700 uppercase tracking-wide">{callConfirm.name}</p>
                  <p className="text-lg font-extrabold font-mono text-green-900 leading-tight">+91 {callConfirm.number}</p>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-2 px-4 pb-5">
              <button
                type="button"
                onClick={() => setCallConfirm(null)}
                className="flex-1 py-3 rounded-lg border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors duration-150"
              >
                ← Go Back
              </button>
              <a
                href={`tel:+91${callConfirm.number}`}
                onClick={() => setCallConfirm(null)}
                className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm text-center flex items-center justify-center gap-2 transition-colors duration-150"
              >
                <Phone size={16} />
                Accept &amp; Call
              </a>
            </div>

          </div>
        </div>
      )}

      {/* Photo lightbox */}
      {showPhotoLightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setShowPhotoLightbox(false)}
        >
          {ownerPhoto ? (
            <img
              src={ownerPhoto}
              alt={customerName || "Owner"}
              className="max-w-full max-h-full rounded-lg object-contain shadow-md"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="w-64 h-64 rounded-lg bg-red-100 flex items-center justify-center">
              <span className="text-8xl font-extrabold text-red-400">
                {customerName ? customerName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Report Emergency disclaimer modal */}
      {showReportDisclaimer && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowReportDisclaimer(false)}
        >
          <div
            className="bg-white rounded-lg shadow-md max-w-sm w-full p-4 border border-slate-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle size={22} className="text-amber-500 shrink-0" />
              <h3 className="text-base font-bold text-slate-900">{t("public.reportEmergency")}</h3>
            </div>
            <p className="text-sm text-slate-600 mb-2">
              {t("public.reportDisclaimerIntro")}
            </p>
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 mb-3">
              <p className="text-sm text-emerald-800 font-medium mb-0.5">{t("public.govRecognitionTitle")}</p>
              <p className="text-xs text-emerald-700">
                {t("public.govRecognitionDesc")}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowReportDisclaimer(false)}
                className="flex-1 py-2.5 rounded-lg border-2 border-slate-300 text-slate-700 font-semibold text-sm"
              >
                {t("shared.cancel")}
              </button>
              <button
                onClick={() => {
                  setShowReportDisclaimer(false);
                  navigate(`/${locketId}/report`);
                }}
                className="flex-1 py-2.5 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700"
              >
                {t("public.understandReport")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicEmergencyInfoPage;
